"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CallVariable = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _ethTypes = require("@blockwell/eth-types");
var _chains = require("@blockwell/chains");
var _ethcall = require("@blockwell/ethcall");
var _util = require("@blockwell/util");
var _convert = require("../../convert");
var _remeda = require("remeda");
var _types = require("../../types");
var _ErrorValueTypeHandler = require("../../type/ErrorValueTypeHandler");
class CallVariable {
  constructor() {
    (0, _defineProperty2.default)(this, "settings", CallVariable.settings);
    (0, _defineProperty2.default)(this, "type", "call");
    (0, _defineProperty2.default)(this, "label", "Read Smart Contract");
    (0, _defineProperty2.default)(this, "description", "Read data from a smart contract on the blockchain.");
    (0, _defineProperty2.default)(this, "valueType", "value");
  }
  dependencies(params, resolver) {
    let address;
    let chain;
    let contract;
    if (params.contract) {
      var _contract, _contract2;
      contract = resolver.resolveContract(params.contract);
      address = (_contract = contract) === null || _contract === void 0 ? void 0 : _contract.address;
      chain = (0, _chains.toChainId)((_contract2 = contract) === null || _contract2 === void 0 ? void 0 : _contract2.network);
    } else if (params.address) {
      address = resolver.resolveString(params.address);
      if (params.chain) {
        chain = (0, _chains.toChainId)(resolver.resolveString(params.chain));
      } else {
        var _contract3;
        contract = resolver.resolveContract();
        chain = (0, _chains.toChainId)((_contract3 = contract) === null || _contract3 === void 0 ? void 0 : _contract3.network);
      }
    } else {
      var _contract4, _contract5;
      contract = resolver.resolveContract();
      address = (_contract4 = contract) === null || _contract4 === void 0 ? void 0 : _contract4.address;
      chain = (0, _chains.toChainId)((_contract5 = contract) === null || _contract5 === void 0 ? void 0 : _contract5.network);
    }
    let args = [];
    if (params.args) {
      for (let it of params.args) {
        let par;
        if ("value" in it) {
          par = it.value;
        } else {
          par = it;
        }
        args.push(_types.vals.value(resolver.resolve(par)));
      }
    }
    let deps = {
      address,
      chain,
      abi: null,
      args
    };
    if (address !== _ethTypes.ETH_ADDRESS_ZERO) {
      deps.abi = resolver.resolveAbi(params.abi);
    } else {
      deps.abi = params.abi;
    }
    if (params.convert) {
      deps.converter = (0, _convert.converterFromSpec)(resolver, {
        contract
      }, params.convert);
    }
    if (params.updateEvent) {
      let topics;
      if (Array.isArray(params.updateEvent)) {
        topics = params.updateEvent;
      } else {
        topics = [params.updateEvent];
      }
      topics = topics.filter(it => it !== "");
      if (topics.length > 0) {
        deps.updateEvent = topics;
        if (params.updateContract) {
          let contract = resolver.resolveContract(params.updateContract);
          if (contract) {
            deps.updateAddress = contract.address;
            deps.updateChain = (0, _chains.toChainId)(contract.network);
          }
        } else if (address !== _ethTypes.ETH_ADDRESS_ZERO) {
          deps.updateAddress = address;
          deps.updateChain = chain;
        }
      }
    } else if (params.cache) {
      deps.cache = params.cache * 1000;
    }
    return deps;
  }
  getValue(ref, deps, batcher) {
    if (!deps) {
      ref.value = null;
      return;
    }
    const {
      abi,
      address,
      chain,
      args,
      converter,
      cache,
      updateEvent,
      updateChain,
      updateAddress
    } = deps;
    if (!address || !chain) {
      ref.value = null;
      return;
    }
    if (address !== _ethTypes.ETH_ADDRESS_ZERO && abi && typeof abi !== "string") {
      if (abi.inputs.length !== args.length) {
        console.warn("CallVariable args length does not match inputs", abi.name, abi.inputs.map(it => it.type), args);
        if (args.length < abi.inputs.length) {
          ref.value = null;
          return;
        }
      }
    } else if (address === _ethTypes.ETH_ADDRESS_ZERO && typeof abi === "string") {} else {
      ref.value = null;
      return;
    }
    if (args.findIndex(it => it === undefined || it === null) > -1) {
      ref.value = null;
      return;
    }
    let convertFunc = converter === null || converter === void 0 ? void 0 : converter.create(batcher);
    const makeCall = (forceBlock = 0) => {
      let b = batcher.batch(chain);
      let req;
      if (typeof abi === "string") {
        req = b.tryEthBalanceString("value", args[0]);
      } else {
        req = b.setContract([abi], address)
        // Since the requests are batched automatically, force update can set the cache time to 1.
        // If there are multiple blocks that call for the value, they will get batched and deduped together.
        .tryAny(abi.name, "value", args, null, forceBlock ? 1 : cache);
      }
      if (forceBlock) {
        req.tryBlockNumber("__block");
      }
      req.execute().then(res => {
        let {
          value
        } = res;
        if (value !== null && value !== void 0 && value.hasOwnProperty(_ethcall.CALL_ERROR)) {
          // @ts-ignore
          let call = value[_ethcall.CALL_ERROR];
          let code = "call_error";
          let message = "Error in smart contract call: ";
          if (call.code === "INVALID_ARGUMENT") {
            message = "Call failed because of an invalid argument.";
          } else {
            if (call.reason) {
              message += call.reason;
            } else {
              message += call.message;
            }
          }
          ref.value = new _ErrorValueTypeHandler.ValueTypeError(code, message);
        }
        if (convertFunc) {
          if ((0, _remeda.isPromise)(convertFunc)) {
            convertFunc.then(func => {
              ref.value = func(value);
            });
          } else {
            ref.value = convertFunc(value);
          }
        } else {
          ref.value = value;
        }
      }).catch(err => {
        console.error(err);
        ref.value = new _ErrorValueTypeHandler.ValueTypeError(err.code || "unknown_error", err.message, {});
      });
    };
    makeCall();
    if (updateEvent && updateAddress) {
      // Debounce since a single block can have a bunch of the same event (like multiple transfers),
      // but we're only interested in redoing the call once.
      batcher.variableUpdates.listen(updateChain, {
        address: updateAddress,
        topics: updateEvent
      }, (0, _util.debounce)(log => {
        makeCall(log.blockNumber);
      }, 1000, {
        maxWait: 3000
      }));
    }
  }
}
exports.CallVariable = CallVariable;
(0, _defineProperty2.default)(CallVariable, "settings", {
  contract: {
    type: "contract",
    description: "Contract to use.",
    undefinedAuto: true
  },
  abi: {
    type: "abi",
    title: "Function",
    description: "Smart contract function to read from."
  },
  args: {
    type: "args",
    title: "Arguments",
    description: "Arguments for the function."
  },
  convert: {
    type: "convert",
    title: "Conversion",
    description: "Decimal and formatting conversion for the value."
  },
  updateContract: {
    type: "contract",
    description: "Smart contract for update events, if different from the call contract.",
    undefinedAuto: true
  },
  updateEvent: {
    type: "events",
    title: "Update Event",
    description: "Events in the smart contract that should trigger this call to update."
  },
  cache: {
    type: "literal",
    valueType: "number",
    title: "Cache Seconds",
    description: "Custom time to cache results for in seconds."
  }
});
//# sourceMappingURL=CallVariable.js.map